import React, { useCallback, useState } from 'react';
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useGridSettings } from '../../../../../hooks/useGridSettings';

/**
 * PRX-137 — "Hide deleted" on the Prescriptions tab.
 *
 * Fillware returns its deleted prescriptions (status letter D, word
 * `deleted`). Kroll's list already leaves out the Rx it marks entered in error
 * (`IsMistake = 0` in the query), so there the toggle has nothing to do and
 * says so.
 *
 * On by default, saved per user in the grid's own saved view
 * (`useGridSettings`, the `hideDeleted` field), next to the column layout.
 */
export const KROLL_HIDE_DELETED_NOTE =
  'Kroll already leaves out prescriptions entered in error, so there is nothing to hide here.';

/** The rows left once PMS-deleted ones are hidden. The same array when nothing is hidden. */
export function withoutDeleted<T extends { rxStatusWord?: string | null }>(rows: T[], hide: boolean): T[] {
  if (!hide) return rows;
  return rows.filter((rx) => rx.rxStatusWord !== 'deleted');
}

/** The saved toggle, on until the user turns it off. A save failure keeps the in-session choice. */
export function useHideDeletedPreference(gridKey: string, pharmacyId?: string) {
  const { entry, save } = useGridSettings(gridKey, pharmacyId);
  const [local, setLocal] = useState<boolean | null>(null);
  const hideDeleted = local ?? entry?.hideDeleted ?? true;
  const setHideDeleted = useCallback(
    (value: boolean) => {
      setLocal(value);
      save({ hideDeleted: value });
    },
    [save],
  );
  return { hideDeleted, setHideDeleted };
}

export interface HideDeletedToggleProps {
  databaseType: string | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const HideDeletedToggle: React.FC<HideDeletedToggleProps> = ({ databaseType, checked, onChange }) => {
  const isKroll = databaseType === 'kroll';
  const control = (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={isKroll ? true : checked}
          disabled={isKroll}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label="Hide deleted"
      sx={{ mr: 0 }}
    />
  );
  if (!isKroll) return control;
  return (
    <Tooltip describeChild title={KROLL_HIDE_DELETED_NOTE}>
      <span tabIndex={0} data-testid="hide-deleted-kroll-note">
        {control}
      </span>
    </Tooltip>
  );
};

export default HideDeletedToggle;
